import { useState } from 'react';
import { ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { formatBytes } from '@/lib/format';
import { cn } from '@/lib/cn';
import { say } from '@/lib/vocabulary';
import type { Shipping } from '@/pages/shipping';

/**
 * The folders this package would hand over.
 *
 * This tree is the structure route's, built from the sealed walk's tools and
 * strays. The tree that actually ships is the delivery manifest, which reads
 * CANONICAL dispositions only. The two agree once the population is
 * registered and disagree completely before it, so the shipping state is
 * drawn here rather than left for the buyer to discover.
 *
 * The structure route has always returned the built tree - every folder, its
 * path, its file count and its size - and the packaging screen showed only the
 * grouping chips that produce it. An operator could choose how folders are
 * organised without ever seeing the folders.
 *
 * Sizes and counts are the backend's own. Every node carries the rule that put
 * its objects there, shown as the reason it holds what it holds - it was once
 * badged "moved by a rule", which read as an override on all sixteen rows when
 * in fact every folder has one.
 *
 * Nothing is hidden: a truncated tree says so, and folding a pass-through level
 * keeps its path.
 */

export interface TreeNode {
  name: string;
  path: string;
  objects: number;
  bytes: number;
  rule?: string;
  flag?: string;
  children?: TreeNode[];
}

/**
 * A rule as words, when it is a token, and untouched when it is not.
 *
 * The rules this field carries are sometimes an enum the vocabulary knows
 * (BY_TOOL) and sometimes a rule expression the backend composed. Translating
 * everything would mangle the second; translating nothing printed BY_TOOL in a
 * folder tree a buyer opens, one row above a flag that WAS translated.
 */
function ruleWords(rule: string): string {
  return /^[A-Z][A-Z0-9_]*$/.test(rule) ? say(rule) : rule;
}

export function readTree(value: unknown): { tree: TreeNode[]; unassigned: number; truncated: boolean } | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.tree)) return null;
  const node = (raw: unknown): TreeNode => {
    const r = (raw ?? {}) as Record<string, unknown>;
    return {
      name: String(r.name ?? ''),
      path: String(r.path ?? ''),
      objects: typeof r.objects === 'number' ? r.objects : 0,
      bytes: typeof r.bytes === 'number' ? r.bytes : 0,
      rule: typeof r.rule === 'string' ? r.rule : undefined,
      flag: typeof r.flag === 'string' ? r.flag : undefined,
      children: Array.isArray(r.children) ? (r.children as unknown[]).map(node) : undefined,
    };
  };
  return {
    tree: (v.tree as unknown[]).map(node),
    unassigned: typeof v.unassignedObjects === 'number' ? v.unassignedObjects : 0,
    truncated: v.groupsTruncated === true,
  };
}

/**
 * Folds away a level that says nothing.
 *
 * The backend groups by the native container, so a tool whose files all sat at
 * the top of its export gets `figma/` then `root/` holding the same six files.
 * That is a real grouping, and drawn it is one more click to the same number.
 * A sole child carrying exactly its parent's objects is folded into it; the
 * path is kept so nothing about where the files land is lost.
 */
function fold(node: TreeNode): TreeNode {
  let current = node;
  while (
    current.children?.length === 1 &&
    current.children[0]!.objects === current.objects &&
    (current.children[0]!.children?.length ?? 0) === 0
  ) {
    const only = current.children[0]!;
    current = { ...current, path: only.path, children: undefined };
  }
  return { ...current, children: current.children?.map(fold) };
}

function Node({ node, depth, biggest }: { node: TreeNode; depth: number; biggest: number }) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  // Top two levels open, because the shape of the delivery is the point; go
  // deeper only when asked, or a wide tool buries everything else.
  const [open, setOpen] = useState(depth < 1);
  return (
    <>
      <div
        className="flex items-center gap-2 border-b border-line px-4 py-1.5 last:border-0"
        style={{ paddingLeft: 16 + depth * 18 }}
      >
        {hasChildren ? (
          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? `Collapse ${node.name}` : `Expand ${node.name}`}
            className="flex flex-none items-center text-ink-4 transition-colors duration-fast hover:text-ink"
          >
            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden="true" />
          </button>
        ) : (
          <span className="w-3.5 flex-none" />
        )}
        {hasChildren && open ? (
          <FolderOpen className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
        ) : (
          <Folder className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
        )}
        <span className="mono min-w-0 flex-1 truncate text-small text-ink" title={node.path}>
          {node.name || '/'}
        </span>
        {/* Every node carries a rule: it is why these objects are here, not a
            sign one was moved. Badging it read as an override on every row. */}
        {/* The flag beside it was translated and this was not, so one row read
            "no rule matched" and the row above it "BY_TOOL" - the backend's
            token, in a folder tree a buyer opens. */}
        {node.rule && (
          <span className="hidden min-w-0 max-w-[28rem] truncate text-caption text-ink-4 lg:block" title={node.rule}>
            {ruleWords(node.rule)}
          </span>
        )}
        {node.flag && (
          <span className="flex-none rounded border border-line px-1.5 py-0.5 text-caption text-warn">
            {say(node.flag)}
          </span>
        )}
        <span className="hidden w-32 flex-none sm:block">
          <span className="block h-1 w-full overflow-hidden rounded-full bg-panel-2">
            <span
              className="block h-full rounded-full bg-acc"
              style={{ width: `${Math.max((node.objects / biggest) * 100, node.objects > 0 ? 2 : 0)}%` }}
            />
          </span>
        </span>
        <span className="mono w-24 flex-none text-right text-caption text-ink-2">
          {node.objects.toLocaleString()} files
        </span>
        <span className="mono w-20 flex-none text-right text-caption text-ink-3">{formatBytes(node.bytes)}</span>
      </div>
      {open && node.children?.map((c) => <Node key={c.path || c.name} node={c} depth={depth + 1} biggest={biggest} />)}
    </>
  );
}

export function PackageTree({
  tree,
  unassigned,
  truncated,
  shipping,
}: {
  tree: TreeNode[];
  unassigned: number;
  truncated: boolean;
  shipping?: Shipping;
}) {
  const folded = tree.map(fold);
  const biggest = Math.max(...folded.map((n) => n.objects), 1);
  if (tree.length === 0) {
    return <p className="px-4 py-3 text-small text-ink-3">No folders have been built for this package yet.</p>;
  }
  return (
    <>
      {folded.map((n) => (
        <Node key={n.path || n.name} node={n} depth={0} biggest={biggest} />
      ))}
      {/* A delivery ships CANONICAL dispositions and nothing else, so an
          unregistered population makes this whole tree a plan rather than a
          manifest. Saying so here is the difference between an operator
          learning it and a buyer learning it. */}
      {shipping?.known && !shipping.willShip && (
        <p className="border-t border-line bg-warn-soft px-4 py-2.5 text-caption text-warn">
          None of this ships yet. A delivery copies only the files that curation placed, and this run has none:{' '}
          {shipping.detail ?? 'no population is registered'}. Take stock of every file while this run is still structuring, and these folders become the manifest.
        </p>
      )}
      <p className="border-t border-line px-4 py-2.5 text-caption text-ink-3">
        {unassigned > 0 &&
          `${unassigned.toLocaleString()} files are not traced to any software. They ship: they are grouped under _stray by the reason they could not be traced, so the buyer receives every file and can see why each one is where it is. Tracing them moves them from _stray into the folder of the software that made them; it does not decide whether they are handed over. `}
        {truncated && 'The backend truncated this tree, so more folders exist than are drawn. '}
        This is the folder structure. The per-file list is the spreadsheet export, one row for every file.
      </p>
    </>
  );
}
